import React, { useEffect, useState } from 'react';
import { Download, FilePlus2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { ExpenseReport } from '../../types';
import { formatDate, formatCurrencyAmount } from '../../lib/format';
import { Spinner, EmptyState, Modal, Alert, inputClass, labelClass, primaryBtn, secondaryBtn } from '../../components/ui';

export default function ExpenseReports() {
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [period, setPeriod] = useState<'weekly' | 'monthly'>('weekly');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState('');

  const load = () => {
    setLoading(true);
    api
      .get<ExpenseReport[]>('/expenses/reports')
      .then(setReports)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    setError('');
    setGenerated('');
    try {
      const result = await api.post<{ id: number }>('/expenses/export', { period, from: from || undefined, to: to || undefined });
      setGenerated(`Report #${result.id} generated and emailed.`);
      setModalOpen(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold">Expense Reports</h1>
          <p className="text-sm text-gray-500">Weekly and monthly CSV expense reports</p>
        </div>
        <button onClick={() => { setError(''); setGenerated(''); setModalOpen(true); }} className={primaryBtn}>
          <FilePlus2 className="h-4 w-4" /> Generate Report
        </button>
      </div>

      {loading ? (
        <Spinner label="Loading reports..." />
      ) : reports.length === 0 ? (
        <EmptyState message="No expense reports generated yet." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Date Range</th>
                <th className="px-4 py-3">Generated By</th>
                <th className="px-4 py-3">Expenses</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Generated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 capitalize">{r.period}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {formatDate(r.period_start)} – {formatDate(r.period_end)}
                  </td>
                  <td className="px-4 py-3">{r.generated_by_name || '—'}</td>
                  <td className="px-4 py-3">{r.total_expenses}</td>
                  <td className="px-4 py-3 font-medium">{formatCurrencyAmount(r.total_amount, 'NGN')}</td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <a
                      href={r.url}
                      download
                      className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 text-xs hover:bg-gray-50"
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Generate Expense Report">
        {error && <Alert>{error}</Alert>}
        {generated && <Alert type="success">{generated}</Alert>}
        <form onSubmit={generate} className="space-y-4">
          <div>
            <label className={labelClass}>Period *</label>
            <select className={inputClass} value={period} onChange={(e) => setPeriod(e.target.value as 'weekly' | 'monthly')}>
              <option value="weekly">Weekly (last 7 days)</option>
              <option value="monthly">Monthly (current month)</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>From (optional)</label>
              <input type="date" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>To (optional)</label>
              <input type="date" className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            A CSV file will be generated, saved, and emailed to the company address.
          </p>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModalOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={generating} className={primaryBtn}>
              {generating ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
